import React, { createContext, useContext, useState, useEffect } from 'react';

const LanguageContext = createContext();

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within LanguageProvider');
  }
  return context;
};

// Translation dictionary
const translations = {
  en: {
    // Common
    close: 'Close',
    save: 'Save',
    cancel: 'Cancel',
    edit: 'Edit',
    delete: 'Delete',
    search: 'Search',
    filter: 'Filter',
    export: 'Export',
    view: 'View',
    add: 'Add',
    new: 'New',
    actions: 'Actions',
    status: 'Status',
    date: 'Date',
    amount: 'Amount',
    total: 'Total',
    balance: 'Balance',
    description: 'Description',
    
    // Dashboard
    dashboard: 'Dashboard',
    financialAccounting: 'Financial & Accounting',
    financialDescription: 'Complete financial management system with double-entry bookkeeping, ZATCA compliance, and real-time reconciliation',
    refresh: 'Refresh',
    newEntry: 'New Entry',
    
    // General Ledger
    generalLedger: 'General Ledger',
    chartOfAccounts: 'Chart of Accounts',
    journalEntries: 'Journal Entries',
    trialBalance: 'Trial Balance',
    ledgerEntries: 'Ledger Entries',
    accountCode: 'Account Code',
    accountName: 'Account Name',
    accountType: 'Type',
    currentBalance: 'Current Balance',
    transactionHistory: 'Transaction History',
    viewFullLedger: 'View Full Ledger',
    newJournalEntry: 'New Journal Entry',
    entry: 'Entry',
    debit: 'Debit',
    credit: 'Credit',
    posted: 'Posted',
    draft: 'Draft',
    balanced: 'Balanced',
    outOfBalance: 'Out of Balance',
    
    // Accounts Payable
    accountsPayable: 'Accounts Payable',
    vendor: 'Vendor',
    vendors: 'Vendors',
    vendorSummary: 'Vendor Summary',
    outstandingInvoices: 'Outstanding Invoices',
    recordPayment: 'Record Payment',
    totalPayables: 'Total Payables',
    overdue: 'Overdue',
    current: 'Current',
    lastPayment: 'Last Payment',
    invoiceNumber: 'Invoice #',
    dueDate: 'Due Date',
    paymentMethod: 'Payment Method',
    reference: 'Reference',
    notes: 'Notes',
    poNumber: 'PO Number',
    supplierReference: 'Supplier Reference',
    deliveryNote: 'Delivery Note',
    grnNumber: 'GRN Number',
    supplierInformation: 'Supplier Information',
    
    // Accounts Receivable
    accountsReceivable: 'Accounts Receivable',
    customer: 'Customer',
    customers: 'Customers',
    customerSummary: 'Customer Summary',
    recordReceipt: 'Record Payment Receipt',
    totalReceivables: 'Total Receivables',
    
    // Reconciliation
    reconciliation: 'Reconciliation',
    bankReconciliation: 'Bank Reconciliation',
    inventoryReconciliation: 'Inventory Reconciliation',
    apReconciliation: 'AP Reconciliation',
    arReconciliation: 'AR Reconciliation',
    reconcileNow: 'Reconcile Now',
    reconciliationProcess: 'Reconciliation Process',
    processingReconciliation: 'Processing reconciliation...',
    reconciliationCompleted: 'Reconciliation completed',
    reconcilingAccounts: 'Reconciling Accounts...',
    
    // Tax & ZATCA
    taxZatca: 'Tax & ZATCA',
    
    // Asset Management
    assetManagement: 'Asset Management',
    fixedAssets: 'Fixed Assets',
    depreciationSchedule: 'Depreciation Schedule',
    assetDisposals: 'Asset Disposals',
    addAsset: 'Add Asset',
    runDepreciation: 'Run Depreciation',
    totalAssets: 'Total Assets',
    accumulatedDepreciation: 'Accumulated Depreciation',
    netBookValue: 'Net Book Value',
    monthlyDepreciation: 'Monthly Depreciation',
    purchaseCost: 'Purchase Cost',
    currentValue: 'Current Value',
    purchaseDate: 'Purchase Date',
    location: 'Location',
    viewInAssetsModule: 'View in Assets Module',
    
    // Side Menu
    more: 'More',
    postingRules: 'Posting Rules',
    closingAudit: 'Closing & Audit',
    settings: 'Settings',
    
    // Language
    language: 'Language',
    english: 'English',
    arabic: 'العربية',
    
    // Chart of Accounts
    accountCode: 'Account Code',
    accountName: 'Account Name',
    type: 'Type',
    balance: 'Balance',
    
    // Journal Entry Form
    newJournalEntry: 'New Journal Entry',
    editJournalEntry: 'Edit Journal Entry',
    journalEntry: 'Journal Entry',
    reference: 'Reference',
    addLine: 'Add Line',
    saveAsDraft: 'Save as Draft',
    postEntry: 'Post Entry',
    total: 'Total',
    
    // Vendor/Customer Details
    contactInformation: 'Contact Information',
    contactPerson: 'Contact Person',
    purchaseHistory: 'Purchase History',
    salesHistory: 'Sales History',
    paymentHistory: 'Payment History',
    filesDocuments: 'Files & Documents',
    totalPurchases: 'Total Purchases',
    totalSales: 'Total Sales',
    openInvoices: 'Open Invoices',
    
    // Payment/Receipt Forms
    vendor: 'Vendor',
    customer: 'Customer',
    invoice: 'Invoice',
    invoiceOptional: 'Invoice (Optional)',
    paymentDate: 'Payment Date',
    amountSAR: 'Amount (SAR)',
    optional: 'Optional',
    selectVendor: 'Select Vendor',
    selectCustomer: 'Select Customer',
    selectInvoice: 'Select Invoice',
    partialPayment: 'Select Invoice (or leave blank for partial payment)',
    
    // Tables
    actions: 'Actions',
    lastPayment: 'Last Payment',
    totalBalance: 'Total Balance',
    days30: '1-30 Days',
    days60: '31-60 Days',
    days90: '61-90 Days',
    
    // Asset Management
    assetID: 'Asset ID',
    assetName: 'Asset Name',
    category: 'Category',
    purchaseDate: 'Purchase Date',
    purchaseCost: 'Purchase Cost',
    accumulatedDepreciation: 'Accumulated Depreciation',
    currentValue: 'Current Value',
    monthlyDepreciation: 'Monthly Depreciation',
    depreciationMethod: 'Depreciation Method',
    usefulLife: 'Useful Life',
    months: 'months',
    allCategories: 'All Categories',
    
    // Reconciliation
    account: 'Account',
    bank: 'Bank',
    statementDate: 'Statement Date',
    statementBalance: 'Statement Balance',
    bookBalance: 'Book Balance',
    difference: 'Difference',
    outstandingItems: 'Outstanding Items',
    lastReconciled: 'Last Reconciled',
    module: 'Module',
    glAccount: 'GL Account',
    wmsQuantity: 'WMS Quantity',
    glValue: 'GL Value',
    wmsValue: 'WMS Value',
    variance: 'Variance',
    
    // Common table headers
    date: 'Date',
    dueDate: 'Due Date',
    aging: 'Aging',
    days: 'days',
    
    // Modals
    viewDetails: 'View Details',
    editAsset: 'Edit Asset',
    assetManagementIntegration: 'Asset Management Integration',
    assetFormNote: 'To add or edit assets with full functionality, use the Assets module. This form creates a financial record only.',
    openAssetsModule: 'Open Assets Module →',
    updateAsset: 'Update Asset',
    
    // Depreciation
    runningDepreciation: 'Running Depreciation...',
    depreciationCompleted: 'Depreciation Completed',
    depreciationNote: 'Depreciation has been calculated and journal entries have been created for all assets.',
    
    // Filter Modal
    typeFilter: 'Type',
    allTypes: 'All Types',
    fromDate: 'From Date',
    toDate: 'To Date',
    clear: 'Clear',
    apply: 'Apply',
    
    // Account Detail Modal
    code: 'Code',
    transactionHistory: 'Transaction History',
    
    // Vendor/Customer Detail Modal
    vendorID: 'Vendor ID',
    customerID: 'Customer ID',
    outstandingBalance: 'Outstanding Balance',
    
    // Payment Form
    recordPayment: 'Record Payment',
    recordReceipt: 'Record Payment Receipt',
    paymentMethod: 'Payment Method',
    referenceNumber: 'Reference Number',
    additionalNotes: 'Additional notes...',
    recordPaymentButton: 'Record Payment',
    recordReceiptButton: 'Record Receipt',
    
    // New Entry Modal
    createManualJournalEntry: 'Create a manual journal entry',
    recordVendorPayment: 'Record a vendor payment',
    recordCustomerPayment: 'Record a customer payment',
    registerNewFixedAsset: 'Register a new fixed asset',
    
    // Additional strings
    selectDifferentAccount: 'Select Different Account',
    journalEntryMustBalance: 'Journal entry must balance! Debits and credits must be equal.',
    journalEntrySaved: 'Journal entry saved successfully!',
    selectAccountFromChart: 'Select an account from Chart of Accounts to view its ledger entries.',
    totalDebitsMTD: 'Total Debits (MTD)',
    totalCreditsMTD: 'Total Credits (MTD)',
    allInvoices: 'All Invoices',
    daysOverdue: 'days overdue',
    open: 'Open',
    paid: 'Paid',
    email: 'Email',
    phone: 'Phone',
    address: 'Address',
    method: 'Method',
    fixedAssetsRegister: 'Fixed Assets Register',
    depreciationScheduleMonth: 'Depreciation Schedule - February 2024',
    assetDisposals: 'Asset Disposals',
    accountsPayableReconciliation: 'Accounts Payable Reconciliation',
    accountsReceivableReconciliation: 'Accounts Receivable Reconciliation',
    glBalance: 'GL Balance',
    subledgerBalance: 'Subledger Balance',
    unmatchedInvoices: 'Unmatched Invoices',
    unmatchedReceipts: 'Unmatched Receipts',
    
    // Payment methods
    bankTransfer: 'Bank Transfer',
    check: 'Check',
    cash: 'Cash',
    creditCard: 'Credit Card',
    
    // Form placeholders and labels
    searchCustomersInvoices: 'Search customers, invoices...',
    searchAssets: 'Search assets...',
    selectInvoiceOrLeaveBlank: 'Select Invoice (or leave blank for partial payment)',
    balanceSAR: 'Balance: SAR',
    paymentRecordedSuccessfully: 'Payment recorded successfully!',
    paymentReceiptRecordedSuccessfully: 'Payment receipt recorded successfully!',
    pleaseFillAllFields: 'Please fill in all required fields',
    download: 'Download',
    
    // Asset categories
    property: 'Property',
    vehicles: 'Vehicles',
    machinery: 'Machinery',
    furniture: 'Furniture',
    itEquipment: 'IT Equipment',
    
    // Asset Management
    noAssetDisposals: 'No asset disposals recorded.',
    assetSaved: 'Asset saved! In production, this would sync with Assets module.',
    depreciationRunCompleted: 'Depreciation run completed! Journal entries created.',
    assetFormPlaceholder: 'Asset form would be displayed here. For full asset management features, please use the Assets module.',
    
    // Reconciliation
    matched: 'Matched',
    pending: 'Pending',
    bankAccountsReconciliation: 'Bank Accounts Reconciliation',
    inventoryModuleReconciliation: 'Inventory Module Reconciliation',
    reconcilingAccounts: 'Reconciling Accounts...',
    comparingStatements: 'Comparing bank statements, inventory records, and subledger balances with the general ledger.',
    processingBankAccounts: 'Processing bank accounts...',
    processingInventoryRecords: 'Processing inventory records...',
    matchingTransactions: 'Matching transactions...',
    totalAccounts: 'Total Accounts',
    matchedItems: 'Matched Items',
    reconciliationDetails: 'Reconciliation Details',
    outstandingItemsFound: 'outstanding items',
    unmatchedItemsFound: 'Unmatched Items Found',
    itemsRequireReview: 'items require manual review. Please check the reconciliation details above.',
    reviewUnmatchedItems: 'Review Unmatched Items',
    exportReport: 'Export Report',
    exportingReport: 'Exporting reconciliation report...',
    reconcile: 'Reconcile',
    linkItems: 'Link Items',
    
    // Month names
    month: 'Month',
    asset: 'Asset',
    openingValue: 'Opening Value',
    closingValue: 'Closing Value',
    journalEntry: 'Journal Entry',
    
    // Additional statuses
    active: 'Active',
    inactive: 'Inactive',
  },
  ar: {
    // Common
    close: 'إغلاق',
    save: 'حفظ',
    cancel: 'إلغاء',
    edit: 'تعديل',
    delete: 'حذف',
    search: 'بحث',
    filter: 'تصفية',
    export: 'تصدير',
    view: 'عرض',
    add: 'إضافة',
    new: 'جديد',
    actions: 'الإجراءات',
    status: 'الحالة',
    date: 'التاريخ',
    amount: 'المبلغ',
    total: 'الإجمالي',
    balance: 'الرصيد',
    description: 'الوصف',
    
    // Dashboard
    dashboard: 'لوحة التحكم',
    financialAccounting: 'المالية والمحاسبة',
    financialDescription: 'نظام إدارة مالية كامل مع مسك دفاتر مزدوج القيد، والامتثال لزاتكا، والمصالحة في الوقت الفعلي',
    refresh: 'تحديث',
    newEntry: 'إدخال جديد',
    
    // General Ledger
    generalLedger: 'دفتر الأستاذ العام',
    chartOfAccounts: 'دليل الحسابات',
    journalEntries: 'قيود اليومية',
    trialBalance: 'ميزان المراجعة',
    ledgerEntries: 'قيود دفتر الأستاذ',
    accountCode: 'رمز الحساب',
    accountName: 'اسم الحساب',
    accountType: 'النوع',
    currentBalance: 'الرصيد الحالي',
    transactionHistory: 'سجل المعاملات',
    viewFullLedger: 'عرض دفتر الأستاذ الكامل',
    newJournalEntry: 'قيد يومية جديد',
    entry: 'القيد',
    debit: 'مدين',
    credit: 'دائن',
    posted: 'مقيد',
    draft: 'مسودة',
    balanced: 'متوازن',
    outOfBalance: 'غير متوازن',
    
    // Accounts Payable
    accountsPayable: 'الحسابات الدائنة',
    vendor: 'المورد',
    vendors: 'الموردون',
    vendorSummary: 'ملخص الموردين',
    outstandingInvoices: 'الفواتير المعلقة',
    recordPayment: 'تسجيل الدفع',
    totalPayables: 'إجمالي الدائنين',
    overdue: 'متأخر',
    current: 'حالي',
    lastPayment: 'آخر دفعة',
    invoiceNumber: 'رقم الفاتورة',
    dueDate: 'تاريخ الاستحقاق',
    paymentMethod: 'طريقة الدفع',
    reference: 'المرجع',
    notes: 'ملاحظات',
    poNumber: 'رقم أمر الشراء',
    supplierReference: 'مرجع المورد',
    deliveryNote: 'إيصال التسليم',
    grnNumber: 'رقم إيصال الاستلام',
    supplierInformation: 'معلومات المورد',
    
    // Accounts Receivable
    accountsReceivable: 'الحسابات المدينة',
    customer: 'العميل',
    customers: 'العملاء',
    customerSummary: 'ملخص العملاء',
    recordReceipt: 'تسجيل استلام الدفع',
    totalReceivables: 'إجمالي المدينين',
    
    // Reconciliation
    reconciliation: 'المصالحة',
    bankReconciliation: 'مصالحة البنك',
    inventoryReconciliation: 'مصالحة المخزون',
    apReconciliation: 'مصالحة الحسابات الدائنة',
    arReconciliation: 'مصالحة الحسابات المدينة',
    reconcileNow: 'مصالحة الآن',
    reconciliationProcess: 'عملية المصالحة',
    processingReconciliation: 'معالجة المصالحة...',
    reconciliationCompleted: 'اكتملت المصالحة',
    reconcilingAccounts: 'مصالحة الحسابات...',
    
    // Tax & ZATCA
    taxZatca: 'الضريبة وزاتكا',
    
    // Asset Management
    assetManagement: 'إدارة الأصول',
    fixedAssets: 'الأصول الثابتة',
    depreciationSchedule: 'جدول الإهلاك',
    assetDisposals: 'تصرفات الأصول',
    addAsset: 'إضافة أصل',
    runDepreciation: 'تشغيل الإهلاك',
    totalAssets: 'إجمالي الأصول',
    accumulatedDepreciation: 'الإهلاك المتراكم',
    netBookValue: 'القيمة الدفترية الصافية',
    monthlyDepreciation: 'الإهلاك الشهري',
    purchaseCost: 'تكلفة الشراء',
    currentValue: 'القيمة الحالية',
    purchaseDate: 'تاريخ الشراء',
    location: 'الموقع',
    viewInAssetsModule: 'عرض في وحدة الأصول',
    
    // Side Menu
    more: 'المزيد',
    postingRules: 'قواعد القيد',
    closingAudit: 'الإغلاق والتدقيق',
    settings: 'الإعدادات',
    
    // Language
    language: 'اللغة',
    english: 'English',
    arabic: 'العربية',
    
    // Chart of Accounts
    accountCode: 'رمز الحساب',
    accountName: 'اسم الحساب',
    type: 'النوع',
    balance: 'الرصيد',
    
    // Journal Entry Form
    newJournalEntry: 'قيد يومية جديد',
    editJournalEntry: 'تعديل قيد اليومية',
    journalEntry: 'قيد اليومية',
    reference: 'المرجع',
    addLine: 'إضافة سطر',
    saveAsDraft: 'حفظ كمسودة',
    postEntry: 'قيد الإدخال',
    total: 'الإجمالي',
    
    // Vendor/Customer Details
    contactInformation: 'معلومات الاتصال',
    contactPerson: 'الشخص المسؤول',
    purchaseHistory: 'سجل المشتريات',
    salesHistory: 'سجل المبيعات',
    paymentHistory: 'سجل المدفوعات',
    filesDocuments: 'الملفات والمستندات',
    totalPurchases: 'إجمالي المشتريات',
    totalSales: 'إجمالي المبيعات',
    openInvoices: 'الفواتير المفتوحة',
    
    // Payment/Receipt Forms
    vendor: 'المورد',
    customer: 'العميل',
    invoice: 'الفاتورة',
    invoiceOptional: 'الفاتورة (اختياري)',
    paymentDate: 'تاريخ الدفع',
    amountSAR: 'المبلغ (ريال سعودي)',
    optional: 'اختياري',
    selectVendor: 'اختر المورد',
    selectCustomer: 'اختر العميل',
    selectInvoice: 'اختر الفاتورة',
    partialPayment: 'اختر الفاتورة (أو اتركه فارغاً للدفع الجزئي)',
    
    // Tables
    actions: 'الإجراءات',
    lastPayment: 'آخر دفعة',
    totalBalance: 'إجمالي الرصيد',
    days30: '1-30 يوم',
    days60: '31-60 يوم',
    days90: '61-90 يوم',
    
    // Asset Management
    assetID: 'معرف الأصل',
    assetName: 'اسم الأصل',
    category: 'الفئة',
    purchaseDate: 'تاريخ الشراء',
    purchaseCost: 'تكلفة الشراء',
    accumulatedDepreciation: 'الإهلاك المتراكم',
    currentValue: 'القيمة الحالية',
    monthlyDepreciation: 'الإهلاك الشهري',
    depreciationMethod: 'طريقة الإهلاك',
    usefulLife: 'العمر الإنتاجي',
    months: 'شهر',
    allCategories: 'جميع الفئات',
    
    // Reconciliation
    account: 'الحساب',
    bank: 'البنك',
    statementDate: 'تاريخ الكشف',
    statementBalance: 'رصيد الكشف',
    bookBalance: 'الرصيد الدفتري',
    difference: 'الفرق',
    outstandingItems: 'العناصر المعلقة',
    lastReconciled: 'آخر مصالحة',
    module: 'الوحدة',
    glAccount: 'حساب دفتر الأستاذ',
    wmsQuantity: 'كمية نظام إدارة المستودعات',
    glValue: 'قيمة دفتر الأستاذ',
    wmsValue: 'قيمة نظام إدارة المستودعات',
    variance: 'التباين',
    
    // Common table headers
    date: 'التاريخ',
    dueDate: 'تاريخ الاستحقاق',
    aging: 'التقادم',
    days: 'أيام',
    
    // Modals
    viewDetails: 'عرض التفاصيل',
    editAsset: 'تعديل الأصل',
    assetManagementIntegration: 'تكامل إدارة الأصول',
    assetFormNote: 'لإضافة أو تعديل الأصول مع الوظائف الكاملة، استخدم وحدة الأصول. هذا النموذج ينشئ سجل مالي فقط.',
    openAssetsModule: 'فتح وحدة الأصول →',
    updateAsset: 'تحديث الأصل',
    
    // Depreciation
    runningDepreciation: 'تشغيل الإهلاك...',
    depreciationCompleted: 'اكتمل الإهلاك',
    depreciationNote: 'تم حساب الإهلاك وإنشاء قيود اليومية لجميع الأصول.',
    
    // Filter Modal
    typeFilter: 'النوع',
    allTypes: 'جميع الأنواع',
    fromDate: 'من تاريخ',
    toDate: 'إلى تاريخ',
    clear: 'مسح',
    apply: 'تطبيق',
    
    // Account Detail Modal
    code: 'الرمز',
    transactionHistory: 'سجل المعاملات',
    
    // Vendor/Customer Detail Modal
    vendorID: 'معرف المورد',
    customerID: 'معرف العميل',
    outstandingBalance: 'الرصيد المعلق',
    
    // Payment Form
    recordPayment: 'تسجيل الدفع',
    recordReceipt: 'تسجيل استلام الدفع',
    paymentMethod: 'طريقة الدفع',
    referenceNumber: 'رقم المرجع',
    additionalNotes: 'ملاحظات إضافية...',
    recordPaymentButton: 'تسجيل الدفع',
    recordReceiptButton: 'تسجيل الاستلام',
    
    // New Entry Modal
    createManualJournalEntry: 'إنشاء قيد يومية يدوي',
    recordVendorPayment: 'تسجيل دفعة مورد',
    recordCustomerPayment: 'تسجيل دفعة عميل',
    registerNewFixedAsset: 'تسجيل أصل ثابت جديد',
    
    // Additional strings
    selectDifferentAccount: 'اختر حساباً مختلفاً',
    journalEntryMustBalance: 'يجب أن يكون قيد اليومية متوازناً! يجب أن يكون المدين والدائن متساويين.',
    journalEntrySaved: 'تم حفظ قيد اليومية بنجاح!',
    selectAccountFromChart: 'اختر حساباً من دليل الحسابات لعرض قيود دفتر الأستاذ الخاصة به.',
    totalDebitsMTD: 'إجمالي المدين (هذا الشهر)',
    totalCreditsMTD: 'إجمالي الدائن (هذا الشهر)',
    allInvoices: 'جميع الفواتير',
    daysOverdue: 'أيام متأخرة',
    open: 'مفتوح',
    paid: 'مدفوع',
    email: 'البريد الإلكتروني',
    phone: 'الهاتف',
    address: 'العنوان',
    method: 'الطريقة',
    fixedAssetsRegister: 'سجل الأصول الثابتة',
    depreciationScheduleMonth: 'جدول الإهلاك - فبراير 2024',
    assetDisposals: 'تصرفات الأصول',
    accountsPayableReconciliation: 'مصالحة الحسابات الدائنة',
    accountsReceivableReconciliation: 'مصالحة الحسابات المدينة',
    glBalance: 'رصيد دفتر الأستاذ',
    subledgerBalance: 'رصيد دفتر الأستاذ الفرعي',
    unmatchedInvoices: 'فواتير غير متطابقة',
    unmatchedReceipts: 'إيصالات غير متطابقة',
    
    // Payment methods
    bankTransfer: 'تحويل بنكي',
    check: 'شيك',
    cash: 'نقد',
    creditCard: 'بطاقة ائتمانية',
    
    // Form placeholders and labels
    searchCustomersInvoices: 'بحث العملاء والفواتير...',
    searchAssets: 'بحث الأصول...',
    selectInvoiceOrLeaveBlank: 'اختر الفاتورة (أو اتركه فارغاً للدفع الجزئي)',
    balanceSAR: 'الرصيد: ريال سعودي',
    paymentRecordedSuccessfully: 'تم تسجيل الدفع بنجاح!',
    paymentReceiptRecordedSuccessfully: 'تم تسجيل استلام الدفع بنجاح!',
    pleaseFillAllFields: 'يرجى ملء جميع الحقول المطلوبة',
    download: 'تحميل',
    
    // Asset categories
    property: 'عقار',
    vehicles: 'مركبات',
    machinery: 'آلات',
    furniture: 'أثاث',
    itEquipment: 'معدات تقنية',
    
    // Asset Management
    noAssetDisposals: 'لم يتم تسجيل أي تصرفات أصول.',
    assetSaved: 'تم حفظ الأصل! في الإنتاج، سيتم مزامنة هذا مع وحدة الأصول.',
    depreciationRunCompleted: 'اكتمل تشغيل الإهلاك! تم إنشاء قيود اليومية.',
    assetFormPlaceholder: 'سيتم عرض نموذج الأصل هنا. للحصول على ميزات إدارة الأصول الكاملة، يرجى استخدام وحدة الأصول.',
    exportFunctionality: 'وظيفة التصدير - سيتم إنشاء تصدير CSV هنا',
    filterFunctionality: 'وظيفة التصفية - سيتم فتح نافذة التصفية هنا',
    dateRangePicker: 'سيتم فتح منتقي نطاق التاريخ هنا',
    reconcilingAccount: 'جاري التوفيق بين الحساب... سيتم فتح عرض تفاصيل التوفيق.',
    linkingReconciliation: 'جاري ربط التوفيق... سيتم فتح واجهة الربط.',
    openingUnmatchedItems: 'جاري فتح مراجعة العناصر غير المتطابقة...',
    exportingReport: 'جاري تصدير تقرير التوفيق...',
    
    // Reconciliation
    matched: 'متطابق',
    pending: 'معلق',
    bankAccountsReconciliation: 'مصالحة الحسابات البنكية',
    inventoryModuleReconciliation: 'مصالحة وحدة المخزون',
    reconcilingAccounts: 'مصالحة الحسابات...',
    comparingStatements: 'مقارنة كشوفات البنك وسجلات المخزون وأرصدة دفتر الأستاذ الفرعي مع دفتر الأستاذ العام.',
    processingBankAccounts: 'معالجة الحسابات البنكية...',
    processingInventoryRecords: 'معالجة سجلات المخزون...',
    matchingTransactions: 'مطابقة المعاملات...',
    totalAccounts: 'إجمالي الحسابات',
    matchedItems: 'العناصر المتطابقة',
    reconciliationDetails: 'تفاصيل المصالحة',
    outstandingItemsFound: 'عناصر معلقة',
    unmatchedItemsFound: 'تم العثور على عناصر غير متطابقة',
    itemsRequireReview: 'عناصر تتطلب مراجعة يدوية. يرجى التحقق من تفاصيل المصالحة أعلاه.',
    reviewUnmatchedItems: 'مراجعة العناصر غير المتطابقة',
    exportReport: 'تصدير التقرير',
    exportingReport: 'تصدير تقرير المصالحة...',
    reconcile: 'مصالحة',
    linkItems: 'ربط العناصر',
    
    // Month names
    month: 'الشهر',
    asset: 'الأصل',
    openingValue: 'القيمة الافتتاحية',
    closingValue: 'القيمة الختامية',
    journalEntry: 'قيد اليومية',
    
    // Additional statuses
    active: 'نشط',
    inactive: 'غير نشط',
  }
};

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(() => {
    return localStorage.getItem('financialLanguage') || 'en';
  });

  useEffect(() => {
    localStorage.setItem('financialLanguage', language);
    // Don't change document direction - only affect Financial module
  }, [language]);

  const t = (key) => {
    return translations[language]?.[key] || translations.en[key] || key;
  };

  const toggleLanguage = () => {
    setLanguage(prev => prev === 'en' ? 'ar' : 'en');
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, toggleLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};
